/**
 * Case-side read-only view of a single session — sees only notes
 * marked visible_to_case=1 + own state info.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Mast } from "../components/Mast.js";
import { api, API_BASE } from "../lib/api.js";

interface MetricPoint { ts: string; source: string; value: number }

interface Session {
  id: string; case_id: string; trainer_id: string;
  scheduled_for: string; estimated_min: number;
  state: string; started_at: string | null; ended_at: string | null;
  recording_r2_key: string | null;
  case_name: string;
}
interface Note {
  id: number; note_md: string; created_at: string;
}

export default function CaseSessionDetail() {
  const { id } = useParams<{ id: string }>();
  const [s, setS] = useState<Session | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [metrics, setMetrics] = useState<MetricPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      api<{ ok: true; session: Session; notes: Note[] }>(`/api/sessions/${id}`),
      api<{ ok: true; series: MetricPoint[] }>(`/api/sessions/${id}/metrics?bucket_ms=2000`),
    ]).then(([sd, md]) => {
      setS(sd.session); setNotes(sd.notes); setMetrics(md.series);
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading || !s) return <><Mast section="訓練紀錄" /><main className="page muted">載入中…</main></>;

  return (
    <>
      <Mast section="訓練紀錄" />
      <main className="page page--narrow">
        <Link to="/me" style={{ fontSize: "var(--t-sm)", color: "var(--ink-soft)", display: "inline-block", marginBottom: "var(--s-4)" }}>
          ← 回我的訓練
        </Link>

        <section style={{ marginBottom: "var(--s-7)" }}>
          <div className="serial rise rise-1">SESSION · {fmtFull(s.scheduled_for)}</div>
          <h1 className="rise rise-2" style={{ marginTop: "var(--s-2)" }}>
            這次<span className="italic-display" style={{ color: "var(--rust)" }}>訓練</span>的紀錄
          </h1>
          <div className="row rise rise-3" style={{ marginTop: "var(--s-3)", gap: "var(--s-4)" }}>
            <StateChip state={s.state} />
            <span className="muted tabular">{s.estimated_min} 分</span>
            {s.started_at && <span className="muted tabular">開始 {fmtTime(s.started_at)}</span>}
            {s.ended_at   && <span className="muted tabular">結束 {fmtTime(s.ended_at)}</span>}
          </div>
        </section>

        {metrics.length > 0 && (
          <section className="rise rise-4" style={{ marginBottom: "var(--s-7)" }}>
            <div className="section-head">
              <div>
                <div className="eyebrow">Metric trend · {new Set(metrics.map(m => m.source)).size} 個 source · {metrics.length} 點</div>
                <h2>訓練過程的訊號變化</h2>
              </div>
            </div>
            <div className="surface" style={{ padding: "var(--s-5)" }}>
              <MetricTrend points={metrics} />
            </div>
          </section>
        )}

        {s.recording_r2_key && (
          <section className="rise rise-4" style={{ marginBottom: "var(--s-7)" }}>
            <div className="section-head">
              <div>
                <div className="eyebrow">錄影</div>
                <h2>觀看你的訓練錄影</h2>
              </div>
            </div>
            <div className="surface" style={{ padding: "var(--s-5)" }}>
              <RecordingPlayer sessionId={s.id} />
            </div>
          </section>
        )}

        <section className="rise rise-4">
          <div className="section-head">
            <div>
              <div className="eyebrow">訓練師回饋 · {notes.length}</div>
              <h2>給你的回饋</h2>
            </div>
          </div>

          {notes.length === 0 ? (
            <div className="empty">
              <div className="muted">訓練師尚未寫回饋。session 結束後會看到。</div>
            </div>
          ) : (
            <div className="col" style={{ gap: "var(--s-3)" }}>
              {notes.map(n => (
                <article key={n.id} className="surface" style={{ padding: "var(--s-5)", borderLeft: "2px solid var(--rust)" }}>
                  <div className="serial" style={{ marginBottom: "var(--s-2)" }}>
                    {n.created_at}
                  </div>
                  <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.7, fontSize: "var(--t-md)" }}>
                    {n.note_md}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
    </>
  );
}

function StateChip({ state }: { state: string }) {
  if (state === "live")    return <span className="chip chip--olive">進行中</span>;
  if (state === "ended")   return <span className="chip">已完成</span>;
  if (state === "aborted") return <span className="chip chip--danger">中斷</span>;
  if (state === "lobby")   return <span className="chip chip--petrol">準備中</span>;
  return <span className="chip chip--rust">{state}</span>;
}

function fmtFull(s: string) {
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString("zh-TW", { year: "numeric", month: "short", day: "numeric", weekday: "short", hour: "2-digit", minute: "2-digit" });
}
function fmtTime(s: string) { return s.slice(11, 16); }

// Multi-source SVG line chart — one line per source, normalised to its
// own min/max so reward (∈ [0,1]) and alpha (∈ [0,30 µV²]) sit on the
// same canvas without one swallowing the other. Per-source legend
// shows current value + range. Pure SVG, no chart library.
function MetricTrend({ points }: { points: MetricPoint[] }) {
  const W = 800, H = 220, PAD = 28;
  const sources = Array.from(new Set(points.map(p => p.source)));
  const palette = ["#c4471f", "#20534b", "#5e7426", "#b8801a", "#a83323", "#4d3a8a"];

  if (points.length === 0) return <div className="muted">沒有 metric 紀錄。</div>;

  const tsMin = new Date(points[0].ts).getTime();
  const tsMax = new Date(points[points.length - 1].ts).getTime();
  const span  = Math.max(tsMax - tsMin, 1);

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H, background: "var(--paper-deep)", borderRadius: "var(--r-sm)" }}>
        {/* x-axis baseline */}
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="var(--hairline)" strokeWidth={1} />
        <line x1={PAD} y1={PAD}     x2={PAD}     y2={H - PAD} stroke="var(--hairline)" strokeWidth={1} />
        {sources.map((src, idx) => {
          const seriesPts = points.filter(p => p.source === src);
          const vals = seriesPts.map(p => p.value);
          const vMin = Math.min(...vals);
          const vMax = Math.max(...vals);
          const vSpan = Math.max(vMax - vMin, 1e-6);
          const xy = seriesPts.map(p => ({
            x: PAD + ((new Date(p.ts).getTime() - tsMin) / span) * (W - PAD * 2),
            y: H - PAD - ((p.value - vMin) / vSpan) * (H - PAD * 2),
          }));
          const stroke = palette[idx % palette.length];
          // Single-point series renders as a circle so it's visible —
          // a degenerate "M x,y" path draws nothing and the user sees
          // the legend with no line, which looks like a data drop.
          if (xy.length === 1) {
            return <circle key={src} cx={xy[0].x} cy={xy[0].y} r={3} fill={stroke} />;
          }
          const path = xy.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
          return <path key={src} d={path} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />;
        })}
      </svg>
      <div className="row" style={{ flexWrap: "wrap", gap: "var(--s-3)", marginTop: "var(--s-3)" }}>
        {sources.map((src, idx) => {
          const series = points.filter(p => p.source === src);
          const last = series[series.length - 1];
          const vals = series.map(p => p.value);
          return (
            <div key={src} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "var(--t-xs)", fontFamily: "var(--mono)" }}>
              <span style={{ width: 12, height: 3, background: palette[idx % palette.length], borderRadius: 1 }} />
              <span>{src}</span>
              <span className="muted">last {last.value.toFixed(2)} · range [{Math.min(...vals).toFixed(2)}, {Math.max(...vals).toFixed(2)}]</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Same player as SessionDetail; duplicated tiny enough that pulling it
// to a shared component costs more than it saves.
function RecordingPlayer({ sessionId }: { sessionId: string }) {
  const [url, setUrl]     = useState<string | null>(null);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bytes, setBytes] = useState(0);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  async function load() {
    setBusy(true); setError(null);
    try {
      const token = sessionStorage.getItem("cognihome_access_token");
      const r = await fetch(`${API_BASE}/api/sessions/${sessionId}/recording`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      setBytes(blob.size);
      setUrl(URL.createObjectURL(blob));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!url) {
    return (
      <div>
        <button onClick={load} disabled={busy} className="btn btn--ghost">
          {busy ? "載入中…" : "▶ 觀看 / 下載錄影"}
        </button>
        {error && <div className="alert" style={{ marginTop: "var(--s-3)" }}>{error}</div>}
      </div>
    );
  }
  return (
    <div>
      <video src={url} controls style={{ width: "100%", borderRadius: "var(--r-sm)", background: "#000" }} />
      <div className="row" style={{ gap: "var(--s-3)", marginTop: "var(--s-3)", alignItems: "center" }}>
        <a href={url} download={`cognihome-${sessionId}.webm`} className="btn btn--small btn--ghost">下載 .webm</a>
        <span className="muted tabular" style={{ fontSize: "var(--t-xs)" }}>{(bytes / 1024 / 1024).toFixed(1)} MB</span>
      </div>
    </div>
  );
}
